import { FC, HTMLAttributes, SyntheticEvent } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Chip,
  TextField,
  createFilterOptions,
} from '@mui/material';
import {
  useTeamTagsLookupQuery,
  TeamTagOption,
} from 'TeamModule/query/hooks/useTeamTagsLookupQuery';

export interface TeamTagsChangeMeta {
  /** Tags in `next` that originated from the server-side lookup list and should bypass freeform validation. */
  preValidated: Set<string>;
}

interface TeamTagsAutocompleteProps {
  value: string[];
  onChange: (next: string[], meta: TeamTagsChangeMeta) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  /** Override the URL/auth-default tenant for the tag lookup. Use when the form selects a target tenant. */
  tenant?: string;
  /** Override the URL/auth-default realm id for the tag lookup. */
  realmId?: string;
  /** Optional per-tag chip color, e.g. to flag a destructive/special tag. */
  getChipColor?: (tag: string) => 'primary' | 'error' | 'default';
}

const filter = createFilterOptions<TeamTagOption>({
  stringify: (option) => option.tag,
});

const ADD_PREFIX = '__add__:';

export const TeamTagsAutocomplete: FC<TeamTagsAutocompleteProps> = ({
  value,
  onChange,
  label = 'Tags',
  placeholder = 'Select or type a tag',
  helperText,
  error,
  disabled,
  tenant,
  realmId,
  getChipColor,
}) => {
  const { tagOptions, isLoading } = useTeamTagsLookupQuery({ tenant, realmId });

  const optionByTag = new Map(tagOptions.map((o) => [o.tag, o]));
  const selectedOptions: TeamTagOption[] = value.map(
    (tag) => optionByTag.get(tag) ?? { tag, count: 0 },
  );

  const handleChange = (_event: SyntheticEvent, next: (string | TeamTagOption)[]) => {
    const seen = new Set<string>();
    const result: string[] = [];
    const preValidated = new Set<string>();
    for (const entry of next) {
      let tag: string;
      let fromLookup = false;
      if (typeof entry === 'string') {
        tag = entry.trim();
        if (optionByTag.has(tag)) fromLookup = true;
      } else if (entry.tag.startsWith(ADD_PREFIX)) {
        tag = entry.tag.slice(ADD_PREFIX.length).trim();
      } else {
        tag = entry.tag;
        fromLookup = true;
      }
      if (!tag || seen.has(tag)) continue;
      seen.add(tag);
      result.push(tag);
      if (fromLookup) preValidated.add(tag);
    }
    onChange(result, { preValidated });
  };

  return (
    <Autocomplete<TeamTagOption, true, false, true>
      multiple
      freeSolo
      disabled={disabled}
      loading={isLoading}
      options={tagOptions}
      value={selectedOptions}
      onChange={handleChange}
      isOptionEqualToValue={(option, val) => option.tag === val.tag}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.tag)}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const trimmed = params.inputValue.trim();
        const alreadyExists =
          trimmed && (options.some((o) => o.tag === trimmed) || value.includes(trimmed));
        if (trimmed && !alreadyExists) {
          filtered.push({
            tag: `${ADD_PREFIX}${trimmed}`,
            count: 0,
          });
        }
        return filtered;
      }}
      renderOption={(props: HTMLAttributes<HTMLLIElement> & { key?: unknown }, option) => {
        const { key: _key, ...liProps } = props;
        const isNew = option.tag.startsWith(ADD_PREFIX);
        const display = isNew ? option.tag.slice(ADD_PREFIX.length) : option.tag;
        return (
          <li {...liProps} key={option.tag}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              gap={2}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {isNew ? `Add "${display}"` : display}
              </Box>
              {!isNew && (
                <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, flexShrink: 0 }}>
                  {option.count}
                </Box>
              )}
            </Box>
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const tagProps = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={option.tag}
              color={getChipColor ? getChipColor(option.tag) : 'primary'}
              label={option.tag}
            />
          );
        })
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
        />
      )}
      slotProps={{
        paper: {
          // Opaque surface only where the theme's own paper is translucent
          // (the dark variant). Pinning the dark navy in every mode left
          // near-black option text on it in the light themes.
          sx: (theme) => ({
            backgroundColor:
              theme.palette.mode === 'dark' ? '#233044' : theme.palette.background.paper,
            backgroundImage: 'none',
            boxShadow: 3,
          }),
        },
        popper: {
          placement: 'bottom-start',
          sx: {
            zIndex: (theme) => theme.zIndex.modal + 1,
          },
          modifiers: [
            { name: 'flip', enabled: false },
            { name: 'preventOverflow', enabled: false },
          ],
        },
      }}
    />
  );
};

export default TeamTagsAutocomplete;
